import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchProposedObjectiveMetrics,
  fetchPlayerExerciseObjectiveMetrics,
  savePlayerExerciseObjectiveMetrics,
} from "../api/objectiveMetricsApi";
import "./CoachObjectiveMetricPicker.css";

/**
 * Coach picks objective metric kinds for a player's SMART-assigned exercise.
 * Used at exercise validation and for post-validation edits.
 */
export function CoachObjectiveMetricPicker({
  playerUserId,
  exId,
  goalRole = "primary",
  goalLabel = null,
  mode = "validate",
  onChange,
  onSaved,
  compact = false,
}) {
  const { t, i18n } = useTranslation();
  const fr = (i18n.language || "en").startsWith("fr");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = useCallback(async () => {
    if (!playerUserId || !exId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data =
        mode === "edit"
          ? await fetchPlayerExerciseObjectiveMetrics(playerUserId, exId, {
              goalRole,
            })
          : null;
      const prop =
        data?.proposal ||
        (await fetchProposedObjectiveMetrics(exId, {
          playerUserId,
          goalRole,
        }));
      setProposal(prop);
      const existing = data?.selection?.metric_kinds || [];
      const defaults =
        existing.length > 0
          ? existing
          : (prop?.proposed || prop?.suggested_default || []).map((m) =>
              typeof m === "string" ? m : m.kind
            );
      setSelected(defaults.filter(Boolean));
    } catch (err) {
      setError(err?.message || "Failed to load metrics");
    } finally {
      setLoading(false);
    }
  }, [playerUserId, exId, goalRole, mode]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onChange?.(selected);
  }, [selected, onChange]);

  const manualKinds = useMemo(() => {
    const list = proposal?.exercise_kinds || [];
    return list.map((m) => (typeof m === "string" ? { kind: m, label: m } : m));
  }, [proposal]);

  const toggle = (kind) => {
    setSelected((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]
    );
  };

  const handleSave = async () => {
    if (!selected.length) return;
    setSaving(true);
    setError(null);
    try {
      const data = await savePlayerExerciseObjectiveMetrics(
        playerUserId,
        exId,
        {
          goalRole,
          metricKinds: selected,
          source: "coach_picked",
        }
      );
      onSaved?.(data);
    } catch (err) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!playerUserId || !exId) return null;

  const title =
    goalLabel ||
    proposal?.goal_label ||
    (fr ? "Objectif SMART" : "SMART objective");
  const hasOverlap = proposal?.has_overlap !== false && (proposal?.proposed?.length || 0) > 0;

  return (
    <div className={`coach-obj-metrics${compact ? " coach-obj-metrics--compact" : ""}`}>
      <div className="coach-obj-metrics__head">
        <strong>
          {fr ? "Métriques M" : "M metrics"} · {title}
        </strong>
        {mode === "validate" ? (
          <span className="coach-obj-metrics__hint">
            {fr
              ? "Confirmez les métriques alignées sur l'objectif."
              : "Confirm metrics aligned with the objective."}
          </span>
        ) : null}
      </div>

      {loading ? (
        <p className="coach-obj-metrics__muted">{t("loading") || "Loading…"}</p>
      ) : error ? (
        <p className="coach-obj-metrics__error">{error}</p>
      ) : (
        <>
          {!hasOverlap && manualKinds.length ? (
            <p className="coach-obj-metrics__note">
              {fr
                ? "Aucune intersection auto — choisissez parmi les métriques calculables de l'exercice."
                : "No automatic overlap — pick from exercise-computable metrics."}
            </p>
          ) : null}
          <ul className="coach-obj-metrics__list">
            {(hasOverlap ? proposal?.proposed || [] : manualKinds).map((m) => {
              const kind = typeof m === "string" ? m : m.kind;
              const label = typeof m === "string" ? m : m.label || m.kind;
              const checked = selected.includes(kind);
              return (
                <li key={kind}>
                  <label className="coach-obj-metrics__item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(kind)}
                    />
                    <span>{label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          {hasOverlap && manualKinds.length > (proposal?.proposed?.length || 0) ? (
            <details className="coach-obj-metrics__adjust">
              <summary>{fr ? "Ajuster" : "Adjust"}</summary>
              <ul className="coach-obj-metrics__list">
                {manualKinds.map((m) => {
                  const kind = m.kind;
                  if ((proposal?.proposed || []).some((p) => p.kind === kind)) {
                    return null;
                  }
                  return (
                    <li key={kind}>
                      <label className="coach-obj-metrics__item">
                        <input
                          type="checkbox"
                          checked={selected.includes(kind)}
                          onChange={() => toggle(kind)}
                        />
                        <span>{m.label || kind}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </details>
          ) : null}
        </>
      )}

      {mode === "edit" ? (
        <button
          type="button"
          className="coach-obj-metrics__save"
          disabled={saving || !selected.length}
          onClick={handleSave}
        >
          {saving ? t("saving") || "Saving…" : t("save") || "Save"}
        </button>
      ) : null}
    </div>
  );
}

export default CoachObjectiveMetricPicker;
